<script>
import AppPagination from '@/components/common/AppPagination.vue'
import { createB2bCompany, getB2bCompanies, getB2bCompanyCategories, updateB2bCompany } from '@/api/b2b.js'

export default {
  name: 'B2bCompanyListPage',
  components: { AppPagination },
  data() {
    return {
      search: { keyword: '', status: '' },
      items: [],
      categories: [],
      page: 1,
      size: 10,
      total: 0,
      loading: false,
      saving: false,
      dialog: false,
      editTarget: null,
      form: this.emptyForm(),
      statusOptions: [
        { title: '전체', value: '' },
        { title: '운영중', value: 'ACTIVE' },
        { title: '중지', value: 'PAUSED' },
        { title: '종료', value: 'ENDED' },
      ],
    }
  },
  computed: {
    categoryOptions() {
      return [{ title: '분류 없음', value: '' }, ...this.categories.map(c => ({ title: c.categoryName, value: c.b2bCompanyCategoryCode }))]
    },
  },
  mounted() {
    this.fetchCategories()
    this.fetchList()
  },
  methods: {
    emptyForm() {
      return { companyCategoryCode: '', companyName: '', businessNo: '', ceoName: '', businessEmail: '', businessPhone: '', zipCode: '', address1: '', address2: '', authMethod: 'ADMIN_APPROVAL', status: 'ACTIVE', memo: '' }
    },
    async fetchCategories() {
      const res = await getB2bCompanyCategories({ page: 1, size: 100, status: 'Y' })
      this.categories = res.data?.data?.items || []
    },
    async fetchList() {
      this.loading = true
      try {
        const res = await getB2bCompanies({
          keyword: this.search.keyword || undefined,
          status: this.search.status || undefined,
          page: this.page,
          size: this.size,
        })
        this.items = res.data?.data?.items || []
        this.total = res.data?.data?.total || 0
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    openCreate() {
      this.editTarget = null
      this.form = this.emptyForm()
      this.dialog = true
    },
    openEdit(item) {
      this.editTarget = item
      this.form = {
        companyCategoryCode: item.b2bCompanyCategoryCode || '',
        companyName: item.companyName || '',
        businessNo: item.businessNo || '',
        ceoName: item.ceoName || '',
        businessEmail: item.businessEmail || '',
        businessPhone: item.businessPhone || '',
        zipCode: item.zipCode || '',
        address1: item.address1 || '',
        address2: item.address2 || '',
        authMethod: item.authMethod || 'ADMIN_APPROVAL',
        status: item.status || 'ACTIVE',
        memo: item.memo || '',
      }
      this.dialog = true
    },
    async save() {
      if (!this.form.companyName?.trim()) return
      this.saving = true
      try {
        const res = this.editTarget
          ? await updateB2bCompany(this.editTarget.b2bCompanyCode, this.form)
          : await createB2bCompany(this.form)
        if (res.data?.code === 0) {
          this.dialog = false
          this.fetchList()
        }
      } finally {
        this.saving = false
      }
    },
    goDetail(item) {
      this.$router.push(`/b2b/companies/${item.b2bCompanyCode}`)
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="text-h5 font-weight-bold">B2B 업체 관리</div>
        <div class="text-body-2 text-grey mt-1">업체별 특가 상품, 재고, 결제수단을 관리합니다.</div>
      </div>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreate">업체 등록</v-btn>
    </div>

    <v-card rounded="lg" class="mb-5">
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="6"><v-text-field v-model="search.keyword" label="업체 검색" placeholder="업체명, 사업자번호, 이메일" variant="outlined" density="compact" hide-details clearable @keyup.enter="handleSearch" /></v-col>
          <v-col cols="12" md="3"><v-select v-model="search.status" :items="statusOptions" label="상태" variant="outlined" density="compact" hide-details /></v-col>
          <v-col cols="12" md="3" class="d-flex justify-end ga-2"><v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn></v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card rounded="lg">
      <v-card-title class="text-subtitle-1 font-weight-bold">업체 목록 <v-chip size="small" color="primary" label class="ml-2">{{ total }}건</v-chip></v-card-title>
      <v-table density="compact">
        <thead><tr><th>업체명</th><th>분류</th><th>사업자번호</th><th>담당 연락처</th><th>상품수</th><th>상태</th><th class="text-end">관리</th></tr></thead>
        <tbody>
          <tr v-for="item in items" :key="item.b2bCompanyCode" class="cursor-pointer" @click="goDetail(item)">
            <td class="font-weight-bold">{{ item.companyName }}</td>
            <td>{{ item.companyCategoryName || '-' }}</td>
            <td>{{ item.businessNo || '-' }}</td>
            <td>{{ item.businessEmail || item.businessPhone || '-' }}</td>
            <td>{{ item.productCount || 0 }}</td>
            <td><v-chip size="small" color="primary">{{ item.status }}</v-chip></td>
            <td class="text-end" @click.stop>
              <v-btn size="small" variant="text" icon="mdi-pencil" @click="openEdit(item)" />
            </td>
          </tr>
          <tr v-if="!loading && !items.length"><td colspan="7" class="text-center text-grey py-8">등록된 업체가 없습니다.</td></tr>
        </tbody>
      </v-table>
      <div class="px-4 pb-4"><AppPagination :total="total" :page="page" :size="size" @update:page="p => { page = p; fetchList() }" @update:size="s => { size = s; page = 1; fetchList() }" /></div>
    </v-card>

    <v-dialog v-model="dialog" max-width="760">
      <v-card rounded="lg">
        <v-card-title>{{ editTarget ? '업체 수정' : '업체 등록' }}</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6"><v-text-field v-model="form.companyName" label="업체명 *" variant="outlined" /></v-col>
            <v-col cols="12" md="6"><v-select v-model="form.companyCategoryCode" :items="categoryOptions" label="업체분류" variant="outlined" /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="form.businessNo" label="사업자번호" variant="outlined" /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="form.ceoName" label="대표자명" variant="outlined" /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="form.businessEmail" label="대표 이메일" variant="outlined" /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="form.businessPhone" label="대표 연락처" variant="outlined" /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model="form.zipCode" label="우편번호" variant="outlined" /></v-col>
            <v-col cols="12" md="8"><v-text-field v-model="form.address1" label="주소" variant="outlined" /></v-col>
            <v-col cols="12"><v-text-field v-model="form.address2" label="상세주소" variant="outlined" /></v-col>
            <v-col cols="12" md="6"><v-select v-model="form.authMethod" :items="[{ title: '관리자 승인', value: 'ADMIN_APPROVAL' }, { title: '임직원 코드', value: 'EMPLOYEE_CODE' }]" label="인증방식" variant="outlined" /></v-col>
            <v-col cols="12" md="6"><v-select v-model="form.status" :items="statusOptions.filter(o => o.value)" label="상태" variant="outlined" /></v-col>
            <v-col cols="12"><v-textarea v-model="form.memo" label="메모" rows="3" variant="outlined" /></v-col>
          </v-row>
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn variant="text" @click="dialog = false">취소</v-btn><v-btn color="primary" :loading="saving" @click="save">저장</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
